import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const sections = [
  { label: "Collection", id: "collection" },
  { label: "Manifesto", id: "manifesto" },
  { label: "Activities", id: "activities" },
  { label: "Events", id: "events" },
  { label: "Membership", id: "membership" },
  { label: "Reviews", id: "reviews" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  const scrollTo = (e, id) => {
    if (onHome) {
      e.preventDefault();
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-ink/10 bg-cream/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12">
        <a href={onHome ? "#top" : "/"} data-testid="nav-logo" onClick={(e) => scrollTo(e, "top")} className="flex items-center gap-3">
          <img
            src="/images/logo-books.png"
            alt="Purple Space Library logo"
            className="h-10 w-10 rounded-xl object-cover ring-1 ring-ink/10"
          />
          <span className="font-serif text-xl leading-none tracking-tight text-ink">
            Purple Space <span className="italic text-plum">Library</span>
          </span>
        </a>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {sections.map((l) => (
            <a
              key={l.id}
              href={`/#${l.id}`}
              onClick={(e) => scrollTo(e, l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-medium tracking-wide text-ink/70 transition-colors duration-300 hover:text-plum"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/journal"
            data-testid="nav-link-journal"
            className="text-sm font-medium tracking-wide text-ink/70 transition-colors duration-300 hover:text-plum"
          >
            Journal
          </Link>
          <a
            href="/#visit"
            onClick={(e) => scrollTo(e, "visit")}
            data-testid="nav-cta-plan-visit"
            className="rounded-full bg-plum px-6 py-2.5 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-105"
          >
            Plan a Visit
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className="lg:hidden"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-ink/10 bg-cream lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {sections.map((l) => (
                <a
                  key={l.id}
                  href={`/#${l.id}`}
                  onClick={(e) => scrollTo(e, l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="rounded-lg px-3 py-3 text-base font-medium text-ink/80 transition-colors duration-200 hover:bg-sand"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/journal"
                data-testid="nav-mobile-link-journal"
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-medium text-ink/80 transition-colors duration-200 hover:bg-sand"
              >
                Journal
              </Link>
              <a
                href="/#visit"
                onClick={(e) => scrollTo(e, "visit")}
                data-testid="nav-mobile-link-visit"
                className="rounded-lg bg-plum px-3 py-3 text-base font-semibold text-cream"
              >
                Plan a Visit
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
